import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useSearchParams } from 'react-router-dom';

import { ShortcutContext, ShortcutKey } from './types';
import { useKeyboardShortcuts } from './useKeyboardShortcuts';
import { useUserCommunicationsStore } from '../../store';
import { getCurrentPageType, PageType } from '../../utils/pageContext';
import {
  useArchiveUserCommunication,
  useArchiveUserCommunicationForDoBox,
  useMarkAsUnreadUserCommunication,
  useReportAsSpamUserCommunication,
  useTrashUserCommunication,
} from '../UserCommunications';

export function useInboxPreviewActions(
  inboxEntryId?: string,
  skipEffectRef?: React.MutableRefObject<boolean>,
) {
  const { registerShortcut, unregisterShortcut } = useKeyboardShortcuts();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const { handleArchiveUserCommunication } = useArchiveUserCommunication();
  const {
    handleArchiveUserCommunication: handleArchiveUserCommunicationForDoBox,
  } = useArchiveUserCommunicationForDoBox(inboxEntryId);
  const { handleMarkAsUnreadUserCommunication } =
    useMarkAsUnreadUserCommunication();
  const { handleReportAsSpamUserCommunication } =
    useReportAsSpamUserCommunication();
  const { handleTrashUserCommunication } = useTrashUserCommunication();
  const { getUserCommunicationByMessageId } = useUserCommunicationsStore();

  useEffect(() => {
    const archiveHandler = () => {
      if (id) {
        const userCommunication = getUserCommunicationByMessageId(id);
        if (userCommunication) {
          const currentPageType = getCurrentPageType();
          if (currentPageType === PageType.DOBOX) {
            handleArchiveUserCommunicationForDoBox(
              [userCommunication.messageId],
              {
                shouldNavigate: true,
              },
            );
          } else {
            handleArchiveUserCommunication([userCommunication.messageId], {
              shouldNavigate: true,
            });
          }
        }
      }
    };

    const deleteHandler = () => {
      if (id) {
        const userCommunication = getUserCommunicationByMessageId(id);
        if (userCommunication) {
          const currentTab = searchParams.get('tab');
          handleTrashUserCommunication([userCommunication.messageId], {
            currentTab,
          });
        }
      }
    };

    const markAsUnreadHandler = () => {
      if (id) {
        const userCommunication = getUserCommunicationByMessageId(id);
        if (userCommunication) {
          if (skipEffectRef) {
            skipEffectRef.current = true;
          }
          handleMarkAsUnreadUserCommunication(userCommunication.messageId);
          navigate(-1);
        }
      }
    };

    const reportAsSpamHandler = () => {
      if (id) {
        const userCommunication = getUserCommunicationByMessageId(id);
        if (userCommunication) {
          handleReportAsSpamUserCommunication([userCommunication.messageId]);
        }
      }
    };

    registerShortcut({
      key: ShortcutKey.ARCHIVE,
      contexts: [ShortcutContext.INBOX_PREVIEW],
      handler: archiveHandler,
      description: 'Archive current email',
    });

    registerShortcut({
      key: ShortcutKey.ARCHIVE_ALT,
      contexts: [ShortcutContext.INBOX_PREVIEW],
      handler: archiveHandler,
      description: 'Archive current email (Gmail style)',
    });

    registerShortcut({
      key: ShortcutKey.DELETE,
      contexts: [ShortcutContext.INBOX_PREVIEW],
      handler: deleteHandler,
      description: 'Delete current email',
    });

    registerShortcut({
      key: ShortcutKey.BACKSPACE,
      contexts: [ShortcutContext.INBOX_PREVIEW],
      handler: deleteHandler,
      description: 'Delete current email',
    });

    registerShortcut({
      key: ShortcutKey.DELETE_KEY,
      contexts: [ShortcutContext.INBOX_PREVIEW],
      handler: deleteHandler,
      description: 'Delete current email',
    });

    registerShortcut({
      key: ShortcutKey.MARK_AS_UNREAD,
      contexts: [ShortcutContext.INBOX_PREVIEW],
      handler: markAsUnreadHandler,
      description: 'Mark current email as unread',
      shiftKey: true,
    });

    registerShortcut({
      key: ShortcutKey.REPORT_SPAM,
      contexts: [ShortcutContext.INBOX_PREVIEW],
      handler: reportAsSpamHandler,
      description: 'Report current email as spam',
    });

    const ctx = [ShortcutContext.INBOX_PREVIEW];
    return () => {
      unregisterShortcut(ShortcutKey.ARCHIVE, ctx);
      unregisterShortcut(ShortcutKey.ARCHIVE_ALT, ctx);
      unregisterShortcut(ShortcutKey.DELETE, ctx);
      unregisterShortcut(ShortcutKey.BACKSPACE, ctx);
      unregisterShortcut(ShortcutKey.DELETE_KEY, ctx);
      unregisterShortcut(ShortcutKey.MARK_AS_UNREAD, ctx);
      unregisterShortcut(ShortcutKey.REPORT_SPAM, ctx);
    };
  }, [
    registerShortcut,
    unregisterShortcut,
    id,
    handleArchiveUserCommunication,
    handleArchiveUserCommunicationForDoBox,
    handleMarkAsUnreadUserCommunication,
    handleReportAsSpamUserCommunication,
    handleTrashUserCommunication,
    searchParams,
  ]);
}
